import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppContext } from "../../../app/use-app-context.js";
import { StartingTeacherAside } from "../components/StartingTeacherAside.js";
import { StartingTeacherLevelProgress } from "../components/StartingTeacherLevelProgress.js";
import { StartingTeacherSkillProgress } from "../components/StartingTeacherSkillProgress.js";
import { StartingTeacherCompetency } from "../components/StartingTeacherCompetency.js";
import { StartingTeacherOverview } from "../components/StartingTeacherOverview.js";
import { StartingTeacherSkill } from "../components/StartingTeacherSkill.js";
import { LearningRouteToolbar } from "../components/StartingTeacherTourButton.js";
import {
  clearProductTourProgress,
  clearProductTourSessionDismissal,
  isProductTourDismissedForSession,
} from "../components/product-tour-storage.js";
import {
  readStartingTeacherProgress,
  withAchievedTask,
  withActiveTopic,
  withClosedTopic,
  withCompletedSkill,
  withCoachNote,
  withCourse,
  withNote,
  withQuizScore,
  writeStartingTeacherProgress,
} from "../data/starting-teacher-progress.js";
import {
  activeNoteSubject,
  emptyStartingTeacherProgress,
  findCompetency,
  findSkill,
  visibleTopics,
  type StartingTeacherProgress,
} from "../data/starting-teacher-route.js";

export function StartingTeacherRoutePage() {
  const { persona } = useAppContext();
  const employeeId = persona.kind === "employee" ? persona.id : "";
  const [search, setSearch] = useSearchParams();
  const [progress, setProgress] = useState<StartingTeacherProgress>(() => readStartingTeacherProgress(employeeId));
  const competencyId = search.get("competency");
  const skillId = search.get("skill");
  const showOverview = search.get("overview") === "1";

  useEffect(() => {
    setProgress(readStartingTeacherProgress(employeeId));
  }, [employeeId]);

  useEffect(() => {
    if (showOverview || competencyId) return;
    if (!isProductTourDismissedForSession(employeeId)) {
      setSearch({ overview: "1" }, { replace: true });
      return;
    }
    if (!progress.activeCompetencyId || !progress.activeSkillId) return;
    setSearch(
      { competency: progress.activeCompetencyId, skill: progress.activeSkillId },
      { replace: true },
    );
  }, [competencyId, employeeId, progress.activeCompetencyId, progress.activeSkillId, setSearch, showOverview]);

  if (persona.kind !== "employee") return null;

  const save = (next: StartingTeacherProgress) => {
    writeStartingTeacherProgress(employeeId, next);
    setProgress(next);
  };
  const competency = competencyId ? findCompetency(competencyId) : undefined;
  const located = skillId ? findSkill(skillId) : undefined;
  const topics = located ? visibleTopics(located.skill, progress) : [];
  const topicId =
    topics.find((entry) => entry.id === (progress.activeTopicId ?? ""))?.id ?? topics[0]?.id ?? "";

  const openCompetency = (nextCompetencyId: string) => {
    save({ ...progress, activeCompetencyId: nextCompetencyId });
    setSearch({ competency: nextCompetencyId });
  };
  const startTour = () => {
    clearProductTourProgress(employeeId);
    clearProductTourSessionDismissal(employeeId);
    setSearch({ overview: "1", tour: "1" });
  };
  const resetRoute = () => {
    save(emptyStartingTeacherProgress());
    setSearch({ overview: "1" });
  };
  const openSkill = (nextSkillId: string, nextTopicId: string, activate: boolean) => {
    if (!competency) return;
    if (activate) save(withActiveTopic(progress, competency.id, nextSkillId, nextTopicId));
    setSearch({ competency: competency.id, skill: nextSkillId });
  };

  const toolbar = <LearningRouteToolbar onReset={resetRoute} onStartTour={startTour} />;
  const onSkillPage = Boolean(competency && located && located.competency.id === competency.id && topicId);
  const noteSubject = activeNoteSubject(progress, {
    competencyId: competency?.id,
    skillId: onSkillPage ? located?.skill.id : null,
  });
  const saveNote = (subject: typeof noteSubject, text: string, sharedWithCoach: boolean) =>
    save(withCoachNote(progress, subject, text, sharedWithCoach));

  const aside = (
    <StartingTeacherAside
      onSaveMentor={(settings) => save({ ...progress, ...settings, hasMentor: true })}
      onSaveNote={saveNote}
      progress={progress}
      subject={noteSubject}
    />
  );

  if (!competency) {
    return (
      <>
        {toolbar}
        <StartingTeacherOverview
          aside={
            <div className="space-y-4">
              <StartingTeacherLevelProgress progress={progress} />
              {aside}
            </div>
          }
          onContinue={openCompetency}
          onOpen={openCompetency}
          progress={progress}
        />
      </>
    );
  }
  if (!located || located.competency.id !== competency.id || !topicId) {
    return (
      <>
        {toolbar}
        <StartingTeacherCompetency
          aside={
            <div className="space-y-4">
              <StartingTeacherLevelProgress competency={competency} progress={progress} />
              {aside}
            </div>
          }
          competency={competency}
          onOpenSkill={openSkill}
          progress={progress}
        />
      </>
    );
  }
  return (
    <>
      {toolbar}
      <StartingTeacherSkill
      aside={
        <div className="space-y-4">
          <StartingTeacherAside
            onSaveMentor={(settings) => save({ ...progress, ...settings, hasMentor: true })}
            onSaveNote={saveNote}
            progress={progress}
            subject={noteSubject}
          />
          <StartingTeacherSkillProgress progress={progress} skillId={located.skill.id} />
        </div>
      }
      onComplete={(source) => save(withCompletedSkill(progress, located.skill.id, source))}
      onQuizResult={(result) => {
        const scored = withQuizScore(progress, located.skill.id, result.percent);
        save(result.perfect ? withCompletedSkill(scored, located.skill.id, "quiz") : scored);
      }}
      competencyId={competency.id}
      key={topicId}
      onAchieveTask={(taskId) => save(withAchievedTask(progress, located.skill.id, taskId))}
      onCloseTopic={() => save(withClosedTopic(progress, topicId))}
      onSaveNote={(note) => save(withNote(progress, topicId, note))}
      onSelectTopic={(nextTopicId) => {
        save(withActiveTopic(progress, competency.id, located.skill.id, nextTopicId));
      }}
      onSkipCourse={() => save(withCourse(progress, located.skill.id, "skipped"))}
      progress={progress}
      skill={located.skill}
      topicId={topicId}
    />
    </>
  );
}
